// ═══════════════════════════════════════════════════════════════
// XYQAR — Auditor ZKP verifier — verifies HMAC-BLAKE2b match commitments
// Role: auditor / admin / super_admin
// ═══════════════════════════════════════════════════════════════
import * as React from 'react';
import { useXyqarAuth } from '@xyqar/shared-auth';
import { apiGet } from '../lib/api';
import {
  PageTitle, Section, Kpi, Btn, Table, Th, Td,
} from '../components/ui-kit';
import { Search, ShieldCheck, AlertTriangle } from 'lucide-react';

interface ZkpBundle {
  match_id:      string;
  mission_id:    string;
  rank:          number;
  fairness_grade: string;
  created_at:    string;
  zkp: {
    commitment:       string;
    score_commitment: string;
    nonce:            string;
    schema:           string;
    ts:               string;
  } | null;
  verify_instructions: string;
}

export default function ZkpVerifier() {
  const { getToken } = useXyqarAuth();
  const [matchId, setMatchId]   = React.useState('');
  const [result,  setResult]    = React.useState<ZkpBundle | null>(null);
  const [err,     setErr]       = React.useState<string | null>(null);
  const [loading, setLoading]   = React.useState(false);

  const lookup = async () => {
    const id = matchId.trim();
    if (!id) return;
    setLoading(true); setErr(null); setResult(null);
    try {
      const tok = await getToken().catch(() => '');
      const j = await apiGet<ZkpBundle>(`/api/v1/auditor/match/${encodeURIComponent(id)}/zkp`, tok);
      setResult(j);
    } catch (e: any) {
      setErr(e.message ?? 'Not found or access denied');
    } finally {
      setLoading(false);
    }
  };

  const fairnessColor: Record<string, string> = {
    GREEN: 'text-emerald-400', YELLOW: 'text-yellow-400',
    ORANGE: 'text-orange-400', RED: 'text-red-400', BLACK: 'text-red-600',
  };
  const hasBundle = Boolean(result?.zkp);
  const commitmentAge = result?.created_at ? new Date(result.created_at).toLocaleString('fr-FR') : '—';

  return (
    <div>
      <PageTitle
        kicker="ZKP commitments"
        title="Match commitment verifier"
      />

      <Section title="Verifier scope" hint="This screen retrieves a commitment bundle and gives the auditor the exact material needed for offline re-checks.">
        <div className="grid gap-4 md:grid-cols-3">
          <Kpi label="Lookup mode" value="By match ID" hint="one mission_match row at a time" />
          <Kpi label="Bundle status" value={hasBundle ? 'Present' : 'Awaiting lookup'} hint="commitment + score + nonce" accent={hasBundle ? 'green' : undefined} />
          <Kpi label="Re-check path" value="Offline" hint="instructions exported below when a bundle exists" />
        </div>
      </Section>

      <Section
        title="Verify a match"
        hint="Retrieve the HMAC-BLAKE2b-256 commitment bundle for any mission_match row by ID"
      >
        <div className="flex gap-3 mb-6">
          <input
            type="text"
            value={matchId}
            onChange={(e) => setMatchId(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && lookup()}
            placeholder="match ID (integer)…"
            className="flex-1 rounded-md bg-white/5 border border-white/10 px-4 py-3 text-sm text-white placeholder-white/30 focus:outline-none focus:border-white/40 font-mono"
            aria-label="Mission match ID"
          />
          <Btn primary onClick={lookup} disabled={loading || !matchId.trim()}>
            <Search className="h-4 w-4" />
            {loading ? 'Looking up…' : 'Verify'}
          </Btn>
        </div>

        {err && (
          <div className="flex items-start gap-3 rounded-md bg-red-900/20 border border-red-700/30 px-4 py-3 text-red-300 text-sm">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>{err}</span>
          </div>
        )}
      </Section>

      {result && (
        <>
          <Section title="Match metadata">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
              <Kpi label="Match ID"      value={`#${result.match_id}`} hint="BIGSERIAL row ID" />
              <Kpi label="Mission ID"    value={result.mission_id.slice(0, 8) + '…'} />
              <Kpi label="Rank"          value={`#${result.rank}`} />
              <Kpi
                label="Fairness grade"
                value={result.fairness_grade}
                accent={result.fairness_grade === 'GREEN' ? 'green' : result.fairness_grade === 'YELLOW' ? 'amber' : 'red'}
              />
            </div>
            <p className="text-white/40 text-xs font-mono">
              Created: {new Date(result.created_at).toISOString()}
            </p>
          </Section>

          <Section title="Audit posture" hint="The verifier is usable even when the bundle is absent, because absence itself is an auditable state.">
            <div className="grid gap-4 md:grid-cols-3">
              <Kpi label="Commitment" value={hasBundle ? 'sealed' : 'missing'} accent={hasBundle ? 'green' : 'amber'} hint={hasBundle ? 'cryptographic material returned' : 'legacy row or unavailable material'} />
              <Kpi label="Fairness band" value={result.fairness_grade} hint="stored with the match row" accent={result.fairness_grade === 'GREEN' ? 'green' : result.fairness_grade === 'YELLOW' ? 'amber' : 'red'} />
              <Kpi label="Retrieved" value={commitmentAge} hint="auditor-visible fetch time" />
            </div>
          </Section>

          {result.zkp ? (
            <Section title="ZKP commitment bundle" hint={`Schema: ${result.zkp.schema} · ${result.zkp.ts}`}>
              <div className="flex items-center gap-2 mb-4">
                <ShieldCheck className="h-5 w-5 text-emerald-400" />
                <span className="text-emerald-400 text-sm font-medium">Commitment present — cryptographically sealed</span>
              </div>
              <Table>
                <thead>
                  <tr><Th>Field</Th><Th>Value (base64url)</Th></tr>
                </thead>
                <tbody>
                  <tr><Td className="font-mono text-white/60 text-xs">wolf_id commitment</Td><Td className="font-mono text-xs break-all">{result.zkp.commitment}</Td></tr>
                  <tr><Td className="font-mono text-white/60 text-xs">score commitment</Td><Td className="font-mono text-xs break-all">{result.zkp.score_commitment}</Td></tr>
                  <tr><Td className="font-mono text-white/60 text-xs">nonce</Td><Td className="font-mono text-xs break-all">{result.zkp.nonce}</Td></tr>
                  <tr><Td className="font-mono text-white/60 text-xs">schema</Td><Td className="font-mono text-xs">{result.zkp.schema}</Td></tr>
                  <tr><Td className="font-mono text-white/60 text-xs">sealed_at</Td><Td className="font-mono text-xs">{result.zkp.ts}</Td></tr>
                </tbody>
              </Table>

              <div className="mt-6 rounded-md bg-white/[0.03] border border-white/10 p-4">
                <p className="text-white/60 text-xs font-mono uppercase tracking-wider mb-2">Re-verification instructions</p>
                <pre className="text-white/70 text-xs whitespace-pre-wrap font-mono leading-relaxed">{result.verify_instructions}</pre>
              </div>
            </Section>
          ) : (
            <Section title="ZKP commitment">
              <div className="flex items-start gap-3 rounded-md bg-amber-900/20 border border-amber-700/30 px-4 py-3 text-amber-300 text-sm">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                <span>No ZKP commitment for this match — this row predates migration 008 (2026-05-06).</span>
              </div>
            </Section>
          )}

          <Section title="Verification checklist" hint="Use this sequence before certifying that a match can be replayed safely.">
            <div className="grid gap-4 md:grid-cols-3">
              <div className="rounded-md bg-white/5 border border-white/10 p-4 text-sm text-white/80">Check the mission ID, rank, and fairness grade against the reviewed match row.</div>
              <div className="rounded-md bg-white/5 border border-white/10 p-4 text-sm text-white/80">Confirm the commitment bundle contains a nonce, commitment, score commitment, and a stable schema.</div>
              <div className="rounded-md bg-white/5 border border-white/10 p-4 text-sm text-white/80">Run the offline verification snippet with trusted source values instead of trusting this UI alone.</div>
            </div>
          </Section>

          <Section title="How to verify" hint="Reproducible locally — no Xyqar infrastructure required">
            <div className="rounded-md bg-white/[0.03] border border-white/10 p-4">
              <pre className="text-white/70 text-xs whitespace-pre-wrap font-mono leading-relaxed">{`# Python 3.10+ — requires PyNaCl and no other dependency
import hashlib, base64, hmac

SEP = b'\\x1f'
nonce    = base64.urlsafe_b64decode(bundle["nonce"] + "==")
key      = nonce + b"xyqar_match_v1"  # deterministic derive

def verify(field, value):
    msg  = field.encode() + SEP + str(value).encode()
    c    = hmac.new(key, msg, hashlib.blake2b).digest()[:32]
    return base64.urlsafe_b64encode(c).rstrip(b"=").decode()

# check wolf_id commitment
assert verify("wolf_id", "<wolf_id>") == bundle["commitment"]
# check score commitment
assert verify("score", <score>)       == bundle["score_commitment"]
print("✓ commitments verified")`}</pre>
            </div>
          </Section>
        </>
      )}
    </div>
  );
}
